/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { useTimezoneManager } from '@sportninja/common/hooks/useTimezoneManager'
import { t } from '@sportninja/common/i18n'
import { isTimeToBeDefined } from '@sportninja/common/ts-utils/date-utils'
import { getBrowserTimezone } from '@sportninja/common/ts-utils/timezone-display'
import { getDayjsTZDate } from '@sportninja/common/utils/utils'
import { Tooltip } from 'antd'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import permanentDstPlugin from '@sportninja/common/ts-utils/dayjs-permanent-dst'
import { paragraphSmall, weightRegular } from 'src/components/css'

// `getDateAndGameTime` calls `.tz()` and formats the `z` token. Registering the
// plugins here (the house pattern) rather than relying on the app entry having
// run first, so this module works standalone — in a unit test, or imported by a
// row before `index.js` has finished. `extend` is idempotent.
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)
// Keeps a zone that abolished its clock change reading on the offset it
// actually keeps, on a runtime whose database still has the old rule.
dayjs.extend(permanentDstPlugin)

/**
 * Returns the status of a game based on its properties.
 * @param {Object} game - The game object.
 * @param {Object} currentPeriod - The current period object.
 * @param {boolean} isSoccer - Indicates if the game is a soccer game.
 * @returns {string} The game status.
 */
const getGameStatus = (game, currentPeriod, isSoccer = false) => {
  if (!game || !currentPeriod) {
    return ''
  }
  if (game.shootout) {
    return 'Shootout'
  } else if (currentPeriod.isOvertime) {
    return isSoccer ? 'Extra Time' : 'Overtime'
  } else {
    return t('util:capitalize', { text: t('common:final') })
  }
}

/**
 * The viewer's time and the venue's time for a game, plus whether the two
 * differ enough to be worth showing both.
 *
 * The viewer's side names its zone rather than leaving dayjs on the runtime's
 * default. A bare `dayjs(iso)` is resolved by the engine's own timezone
 * database, which the permanent-DST plugin never sees — so from 2026-11-01 a
 * viewer in British Columbia reads a game an hour early and labelled PST,
 * while the venue's side beside it is already corrected. Naming the zone puts
 * both through `.tz()`, which is the seam the plugin patches. Same convention
 * as `useTimezoneManager`'s `userDate` and the event rows' `displayZone`. See
 * ts-utils/dayjs-permanent-dst.
 *
 * A runtime that cannot name its own zone falls back to the untouched local
 * value, exactly as before — `.tz('')` throws, and one time in the wrong zone
 * beats taking the row down.
 */
export const getDateAndGameTime = (game) => {
  const viewerZone = getBrowserTimezone()
  const localDate = viewerZone
    ? dayjs(game.starts_at).tz(viewerZone)
    : dayjs(game.starts_at)
  const date = getDayjsTZDate(game.starts_at, game.venue?.timezone)
  //  Both labels now come from corrected values, so a BC viewer watching a BC
  //  game no longer sees 'PDT' vs 'PST' and gets a redundant second line.
  const displayGameTime = date.format('z') !== localDate.format('z')
  return { localDate, date, displayGameTime }
}

export const getGameStatusLabel = (game_status_id, statusTypes) => {
  if (!statusTypes || !Array.isArray(statusTypes)) {
    return ''
  }
  const status = statusTypes.find((type) => type?.id === game_status_id)
  if (status?.name) {
    return status.name
  }
  return ''
}

export const getCurrentPeriod = (game, isSoccer = false, isHockey = false) => {
  let currentPeriod = {}
  const isGenericGame =
    game?.generic_sport_id !== null && game?.generic_sport_id !== undefined
  if (game?.current_period) {
    const periodName =
      game?.current_period?.period_type?.name ||
      game.current_period.period_type.name_full
    currentPeriod = {
      fullName: game.current_period.period_type.name_full,
      name: game.current_period.period_type.name,
      isOvertime: game?.current_period?.period_type?.is_overtime || false,
    }

    if (game.current_period.clock_time) {
      currentPeriod.clockTime = dayjs(
        game.current_period.clock_time,
        'hh:mm:ss'
      ).format('m:ss')
    }
  }
  return currentPeriod
}

const GameListRowTime = ({
  game,
  gameStatusTypes,
  isSoccer = false,
  isHockey = false,
  isGenericSport = false,
}) => {
  const isGameTimeToBeDefined = isTimeToBeDefined(game.starts_at)
  const currentPeriod = getCurrentPeriod(game, isSoccer, isHockey)
  const {
    userDate,
    locationDate,
    checkLocationDateAndUserDateAreSame,
    getDateString,
  } = useTimezoneManager({
    date: game.starts_at,
    timezone: game?.venue?.timezone,
  })
  const gameStatusLabel = getGameStatusLabel(
    game.game_status_id,
    gameStatusTypes
  )
  const gameStatus = getGameStatus(game, currentPeriod, isSoccer)

  return (
    <>
      <div
        className='game-row-game-time'
        css={css`
          ${paragraphSmall}
          ${weightRegular}
          color: ${colors.NEUTRAL_0};
          margin-top: 4px;
          margin-bottom: 4px;
        `}
      >
        {isGameTimeToBeDefined ? (
          <Tooltip title='Game Time to be Defined'>
            <p>TBD</p>
          </Tooltip>
        ) : (
          <>
            <p>
              {userDate &&
                getDateString(userDate, 'MMMM dd, y - h:mm a (ZZZZ)')}
            </p>
            <p>
              {locationDate &&
                !checkLocationDateAndUserDateAreSame() &&
                getDateString(
                  locationDate,
                  userDate.toFormat('dd') !== locationDate.toFormat('dd')
                    ? 'MMM d - h:mm a ZZZZ'
                    : 'h:mm a ZZZZ'
                )}
            </p>
          </>
        )}
      </div>
      {game.ended_at ? (
        <div
          className='game-row-final-text'
          css={css`
            ${paragraphSmall}
            ${weightRegular}
            color: ${colors.PRIMARY};
            text-transform: uppercase;
          `}
        >
          {gameStatus}
          {game.game_status_id > 1 &&
            gameStatusLabel &&
            gameStatusLabel?.length > 0 &&
            ` - ${gameStatusLabel}`}
          {game.stats_rolledback && (
            <span
              css={css`
                text-transform: capitalize;
              `}
            >
              {' -'} [In Review]
            </span>
          )}
        </div>
      ) : game.started_at && !isGenericSport ? (
        game.shootout ? (
          <div
            className='game-row-current-period-time'
            css={css`
              ${paragraphSmall}
              ${weightRegular}
              color: ${colors.NEUTRAL_0};
              margin-top: 4px;
              text-transform: uppercase;
            `}
          >
            Shootout
          </div>
        ) : (
          <div
            className='game-row-current-period-time'
            css={css`
              ${paragraphSmall}
              ${weightRegular}
              color: ${colors.NEUTRAL_0};
              margin-top: 4px;
              text-transform: uppercase;
            `}
          >
            {currentPeriod.clockTime
              ? currentPeriod.fullName
              : currentPeriod.name}{' '}
            {currentPeriod.clockTime
              ? ` - ${currentPeriod.clockTime}`
              : t('util:capitalize', {
                  text: t('GameScreen:intermission'),
                })}
          </div>
        )
      ) : game.started_at && isGenericSport ? (
        <div
          className='game-row-current-period-time'
          css={css`
            ${paragraphSmall}
            ${weightRegular}
            color: ${colors.NEUTRAL_0};
            margin-top: 4px;
            text-transform: uppercase;
          `}
        >
          In Progress
        </div>
      ) : (
        false
      )}
    </>
  )
}

export default GameListRowTime
